import React from 'react';
import { Link2, X } from 'lucide-react';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface MergedColumnDialogProps {
  columnName: string;
  mergedAttributes: string[];
  onUnmerge: () => void;
  onCancel: () => void;
}

export function MergedColumnDialog({
  columnName,
  mergedAttributes,
  onUnmerge,
  onCancel,
}: MergedColumnDialogProps) {
  const { t } = useLanguage();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        className="rounded-lg shadow-xl p-6 w-[500px]"
        style={{
          backgroundColor: 'var(--md-surface-container-high)',
          color: 'var(--md-on-surface)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5" style={{ color: 'var(--md-primary)' }} />
            <h2 className="text-xl font-semibold">{t('dialogs.mergedColumn.title')}</h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1 rounded hover:bg-opacity-10"
            style={{ backgroundColor: 'var(--md-surface-variant)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Column Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t('dialogs.mergedColumn.columnName')}
          </label>
          <div
            className="px-4 py-2 rounded"
            style={{
              backgroundColor: 'var(--md-surface-variant)',
              color: 'var(--md-on-surface)',
            }}
          >
            {columnName}
          </div>
        </div>

        {/* Merged Attributes */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t('dialogs.mergedColumn.attributes').replace('{count}', mergedAttributes.length.toString())}
          </label>
          <div
            className="rounded p-3 max-h-48 overflow-y-auto"
            style={{
              backgroundColor: 'var(--md-surface)',
              border: '1px solid var(--md-outline-variant)',
            }}
          >
            {mergedAttributes.map((attr, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 py-2 px-3 mb-2 rounded"
                style={{
                  backgroundColor: 'var(--md-surface-container)',
                }}
              >
                <span className="font-mono text-sm">{attr}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div
          className="mb-6 p-3 rounded text-sm"
          style={{
            backgroundColor: 'var(--md-tertiary-container)',
            color: 'var(--md-on-tertiary-container)',
          }}
        >
          {t('dialogs.mergedColumn.info')}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded font-medium transition-colors"
            style={{
              backgroundColor: 'var(--md-surface-variant)',
              color: 'var(--md-on-surface-variant)',
            }}
          >
            {t('common.close')}
          </button>
          <button
            onClick={() => {
              onUnmerge();
              onCancel();
            }}
            className="px-4 py-2 rounded font-medium transition-colors"
            style={{
              backgroundColor: 'var(--md-error)',
              color: 'var(--md-on-error)',
            }}
          >
            {t('dialogs.mergedColumn.unmergeBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
